'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Brain,
  Check,
  ChevronDown,
  FileText,
  Film,
  Gauge,
  ImageIcon,
  Info,
  Loader2,
  RefreshCw,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEditorStore } from '@/lib/editor/editorStore';
import { AGENT_MODELS, getModelById, type AgentModel, type AgentModelTier } from '@/lib/editor/agent/models';
import { CHAT_MARKUP } from '@/lib/editor/agent/pricing';
import type { GenShot } from '@/lib/editor/agent/generation';
import { editorToolActionLabel, editorToolProgressLabel } from '@/lib/editor/agent/toolPresentation';
import { cn } from '@/lib/utils/cn';
import type { EditorChatMessage, MentionRef } from '@/types/editor';
import { ChatComposer } from './ChatComposer';
import { GenerationProposalCard } from './GenerationProposalCard';
import { useEditorAgent } from './hooks/useEditorAgent';

const TIER_ICON: Record<AgentModelTier, LucideIcon> = {
  fast: Zap,
  balanced: Gauge,
  powerful: Brain,
};

const TIER_LABEL: Record<AgentModelTier, string> = {
  fast: 'Быстрые',
  balanced: 'Сбалансированные',
  powerful: 'Мощные',
};

/** Charged price per 1M tokens (base × our markup), for the picker cost hint. */
function pricePer1M(model: AgentModel): { input: string; output: string } {
  return {
    input: `${(model.usdPer1kInput * 1000 * CHAT_MARKUP).toFixed(2)}`,
    output: `${(model.usdPer1kOutput * 1000 * CHAT_MARKUP).toFixed(2)}`,
  };
}

const TIER_ORDER: AgentModelTier[] = ['fast', 'balanced', 'powerful'];

interface EditorChatSidebarProps {
  projectId: string;
}

interface ToolActionStatus {
  id: string;
  label: string;
  state: 'running' | 'completed' | 'error';
}

/** One chat bubble — user (right, brand) or assistant (left, glass). */
function ChatBubble({
  role,
  text,
  actions,
  mentions,
  attachments,
}: {
  role: 'user' | 'assistant';
  text: string;
  actions?: ToolActionStatus[];
  mentions?: MentionRef[];
  attachments?: EditorChatMessage['attachments'];
}) {
  const isUser = role === 'user';
  const hasMentionsOrAttachments = (mentions && mentions.length > 0) || (attachments && attachments.length > 0);
  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-brand-300">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
      )}
      <div className={cn('flex min-w-0 max-w-[82%] flex-col gap-1.5', isUser ? 'items-end' : 'items-start')}>
        {text && (
          <div
            className={cn(
              'w-fit whitespace-pre-wrap break-words px-3.5 py-2 text-[13px] leading-relaxed',
              isUser
                ? 'rounded-2xl rounded-br-sm bg-brand-600 text-white'
                : 'rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.05] text-white/90'
            )}
          >
            {text}
          </div>
        )}
        {!isUser && actions && actions.length > 0 && (
          <div className="w-full min-w-[210px] rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2.5">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">
              {actions.some((action) => action.state === 'running')
                ? 'Выполняю'
                : actions.some((action) => action.state === 'error')
                  ? 'Не получилось'
                  : 'Готово'}
            </div>
            <div className="flex flex-col gap-2">
              {actions.map((action) => (
                <div key={action.id} className="flex items-start gap-2 text-[12px] leading-snug text-white/75">
                  {action.state === 'running' ? (
                    <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-brand-300" strokeWidth={2.2} />
                  ) : action.state === 'error' ? (
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" strokeWidth={2.2} />
                  ) : (
                    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                      <Check className="h-2.5 w-2.5" strokeWidth={2.8} />
                    </span>
                  )}
                  <span>{action.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {hasMentionsOrAttachments && (
          <div className="flex flex-wrap gap-1.5">
            {mentions?.map((m, i) => (
              <span
                key={`m-${m.id}-${i}`}
                className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-white/50"
              >
                <Film className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                <span className="max-w-[110px] truncate">{String(m.title ?? '')}</span>
              </span>
            ))}
            {attachments?.map((a, i) => (
              <span
                key={`a-${a.name}-${i}`}
                className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-white/50"
              >
                {a.kind === 'image' ? (
                  <ImageIcon className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                ) : (
                  <FileText className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                )}
                <span className="max-w-[110px] truncate">{String(a.name ?? '')}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Extract a human-readable tool name from a v5 UIMessage part, defensively. */
function toolNameFromPart(part: unknown): string | null {
  if (!part || typeof part !== 'object') return null;
  const p = part as { type?: unknown; toolName?: unknown };
  if (typeof p.type === 'string' && p.type.startsWith('tool-')) {
    return p.type.slice('tool-'.length) || null;
  }
  if (p.type === 'dynamic-tool' && typeof p.toolName === 'string') {
    return p.toolName;
  }
  return null;
}

function textFromParts(parts: unknown): string {
  if (!Array.isArray(parts)) return '';
  return parts
    .map((part) => {
      if (part && typeof part === 'object' && (part as { type?: unknown }).type === 'text') {
        const text = (part as { text?: unknown }).text;
        return typeof text === 'string' ? text : '';
      }
      return '';
    })
    .join('');
}

function toolActionsFromParts(parts: unknown): ToolActionStatus[] {
  if (!Array.isArray(parts)) return [];
  const actions: ToolActionStatus[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (part && typeof part === 'object' && (part as { type?: unknown }).type === 'text') continue;
    const name = toolNameFromPart(part);
    // proposeGeneration gets its own Allow/Decline card (see
    // `proposeGenerationPartsFrom` below), not a generic tool chip.
    if (!name || name === 'proposeGeneration') continue;
    const p = part as {
      toolCallId?: unknown;
      state?: unknown;
      output?: unknown;
      errorText?: unknown;
    };
    const output = p.output && typeof p.output === 'object'
      ? p.output as { summary?: unknown }
      : null;
    const completedSummary = typeof output?.summary === 'string' && output.summary.trim()
      ? output.summary.trim()
      : editorToolActionLabel(name);
    const isError = p.state === 'output-error' || typeof p.errorText === 'string';
    const isCompleted = p.state === 'output-available';
    actions.push({
      id: typeof p.toolCallId === 'string' ? p.toolCallId : `${name}-${index}`,
      label: isError
        ? 'Не удалось применить изменение'
        : isCompleted
          ? completedSummary
          : editorToolProgressLabel(name),
      state: isError ? 'error' : isCompleted ? 'completed' : 'running',
    });
  }
  return actions;
}

function friendlyAgentError(error: Error): string {
  const message = error.message.toLowerCase();
  if (message.includes('insufficient tokens')) return 'Недостаточно токенов для ответа Canny.';
  if (message.includes('unauthorized')) return 'Сессия истекла. Обновите страницу и войдите снова.';
  if (message.includes('bad request')) return 'Canny не смог продолжить этот запрос.';
  return 'Связь с Canny прервалась. Уже выполненные правки сохранены.';
}

/** One `proposeGeneration` tool-call part extracted defensively from a v5 UIMessage. */
interface ProposeGenerationPart {
  toolCallId: string;
  input: { summary: string; shots: GenShot[] };
  state: 'input-available' | 'output-available';
}

/**
 * Extracts pending/resolved `proposeGeneration` tool-call parts from a
 * message's `parts` array. Handles both the typed `tool-proposeGeneration`
 * shape (client tool with no server `execute`, per `EDITING_TOOLS`) and,
 * defensively, a `dynamic-tool` shape — never throws on an unexpected part.
 * Only `input-available` / `output-available` states carry a usable
 * `.input`; earlier (`input-streaming`) or error states are skipped.
 */
function proposeGenerationPartsFrom(parts: unknown): ProposeGenerationPart[] {
  if (!Array.isArray(parts)) return [];
  const results: ProposeGenerationPart[] = [];
  for (const part of parts) {
    if (!part || typeof part !== 'object') continue;
    const p = part as {
      type?: unknown;
      toolName?: unknown;
      toolCallId?: unknown;
      state?: unknown;
      input?: unknown;
    };
    const isTypedProposeGeneration = p.type === 'tool-proposeGeneration';
    const isDynamicProposeGeneration = p.type === 'dynamic-tool' && p.toolName === 'proposeGeneration';
    if (!isTypedProposeGeneration && !isDynamicProposeGeneration) continue;
    if (p.state !== 'input-available' && p.state !== 'output-available') continue;
    if (typeof p.toolCallId !== 'string') continue;
    if (!p.input || typeof p.input !== 'object') continue;
    const input = p.input as { summary?: unknown; shots?: unknown };
    if (typeof input.summary !== 'string' || !Array.isArray(input.shots)) continue;
    results.push({
      toolCallId: p.toolCallId,
      input: { summary: input.summary, shots: input.shots as GenShot[] },
      state: p.state,
    });
  }
  return results;
}

export function EditorChatSidebar({ projectId }: EditorChatSidebarProps) {
  const { t } = useI18n();
  const { authedFetch } = useAuthToken();
  const modelId = useEditorStore((s) => s.modelId);
  const setModelId = useEditorStore((s) => s.setModelId);
  const genProposals = useEditorStore((s) => s.genProposals);
  const createGenProposal = useEditorStore((s) => s.createGenProposal);
  const allowGenProposal = useEditorStore((s) => s.allowGenProposal);
  const retryGenJob = useEditorStore((s) => s.retryGenJob);
  const declineGenProposal = useEditorStore((s) => s.declineGenProposal);
  const lastBriefReferenceUrl = useEditorStore((s) => s.lastBriefReferenceUrl);
  const { messages, sendMessage, status, error, retry, addToolOutput, lastSent, hydrateHistory } = useEditorAgent(projectId);
  const lastUserMsgId = [...messages].reverse().find((m) => m.role === 'user')?.id;

  const [history, setHistory] = useState<EditorChatMessage[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  // toolCallIds we've already kicked off `createGenProposal` for, from THIS
  // component instance — an extra guard on top of the store's own
  // idempotency check (`genProposals.some(...)`) so a burst of re-renders
  // while a message is still streaming doesn't fire the quote requests
  // (network calls) more than once per proposal.
  const kickedOffProposalsRef = useRef<Set<string>>(new Set());

  const selectedModel = getModelById(modelId);

  // Close the model picker on outside click / Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onEsc);
    };
  }, [pickerOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch(`/api/editor-chats/${projectId}`);
        if (!res.ok) return;
        const data = (await res.json()) as { messages?: EditorChatMessage[] };
        if (!cancelled && Array.isArray(data.messages)) {
          setHistory(data.messages);
          hydrateHistory(data.messages);
        }
      } catch {
        // История не обязательна для работы чата — молча игнорируем сбой загрузки.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch, hydrateHistory, projectId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, history]);

  // Defensive recovery for Canny → Supercomputer generation. The primary
  // path reserves the proposal synchronously in `useEditorAgent.onToolCall`;
  // this effect also recognises an already-resolved UIMessage part so a fast
  // stream/render or future SDK state transition cannot lose the card.
  // Guarded twice:
  // the local ref (below) stops this component from re-issuing quote
  // requests across re-renders, and `createGenProposal` itself no-ops if a
  // proposal for that toolCallId already exists in the store.
  useEffect(() => {
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      const proposeParts = proposeGenerationPartsFrom((m as { parts?: unknown }).parts);
      for (const part of proposeParts) {
        if (kickedOffProposalsRef.current.has(part.toolCallId)) continue;
        if (genProposals.some((p) => p.toolCallId === part.toolCallId)) continue;
        kickedOffProposalsRef.current.add(part.toolCallId);
        void createGenProposal(
          part.toolCallId,
          part.input.summary,
          part.input.shots,
          lastBriefReferenceUrl,
          authedFetch
        );
      }
    }
    // Deliberately depends only on `messages`: `genProposals` is read fresh
    // via closure each run (this effect re-runs on every streamed chunk),
    // and re-running it because `createGenProposal`/`authedFetch`/
    // `lastBriefReferenceUrl` changed identity would add nothing — the ref
    // guard already makes re-invocation for the same toolCallId a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const isBusy = status === 'streaming' || status === 'submitted';

  // Canny → Supercomputer generation: the Allow/Decline DECISION on a
  // `proposeGeneration` proposal must happen before the next message is
  // sent, even though the generation itself runs in the background (see
  // the poller) and is deliberately non-blocking. Two failure modes if we
  // let a new message through first: (1) `addToolOutput` (ai@5.0.210) only
  // patches parts in the LAST message, so once a new user message is
  // appended, the pending tool call can never be resolved — Allow/Decline
  // silently no-ops forever; (2) the next `/api/editor-agent` request would
  // serialize that unresolved tool call with no matching tool-result and
  // the provider 400s, corrupting the chat. So: block sending while ANY
  // proposal is undecided — checked two ways, since a proposal can be
  // "pending" in the store (`genProposals`) slightly after its tool-call
  // part has already streamed in as `input-available` but before the
  // effect above has kicked off `createGenProposal` for it.
  const hasPendingProposal =
    genProposals.some((p) => p.decided === 'pending') ||
    messages.some((m) => {
      if (m.role !== 'assistant') return false;
      const proposeParts = proposeGenerationPartsFrom((m as { parts?: unknown }).parts);
      return proposeParts.some((part) => part.state === 'input-available');
    });

  // Total USD spent on this chat so far (from persisted history usage).
  const spentUsd = history.reduce(
    (sum, m) => sum + (m.role === 'assistant' ? m.usage?.costUsd ?? 0 : 0),
    0
  );
  // Total USD spent on Canny → Supercomputer generation: sum of `costUsd`
  // across COMPLETED jobs only — `quoting`/`awaiting` carry an unconfirmed
  // estimate (proposal may still be pending/declined) and `running` jobs
  // are charged but not yet delivered, so neither belongs in "spent" yet.
  const spentGenUsd = genProposals.reduce(
    (sum, p) =>
      sum + p.jobs.reduce((jobSum, j) => jobSum + (j.state === 'completed' ? j.costUsd ?? 0 : 0), 0),
    0
  );
  const embeddedProposalIds = new Set(
    messages.flatMap((message) =>
      message.role === 'assistant'
        ? proposeGenerationPartsFrom((message as { parts?: unknown }).parts).map((part) => part.toolCallId)
        : []
    )
  );
  // Persisted tasks outlive AI SDK tool parts. Render those project-level
  // tasks independently so running jobs remain visible and a pending plan
  // can still be approved after a full page reload.
  const restoredProposals = genProposals.filter((proposal) => !embeddedProposalIds.has(proposal.toolCallId));

  const TriggerTierIcon = selectedModel ? TIER_ICON[selectedModel.tier] : Zap;

  return (
    <div className="flex h-full flex-col bg-[#0d0717]">
      {/* header — fixed bar; border + downward shadow signal content scrolls behind it */}
      <div className="z-10 flex items-center justify-between gap-2 border-b border-white/[0.06] px-4 py-3 shadow-[0_10px_24px_-12px_rgba(0,0,0,0.75)]">
        <span className="truncate text-[15px] font-semibold text-white">{t('studio.editor.chat.title')}</span>

        {/* Custom model picker (dark, V2 style) */}
        <div className="relative shrink-0" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 py-1.5 pl-2 pr-1.5 text-xs text-white/85 transition hover:bg-white/10"
          >
            <TriggerTierIcon className="h-3.5 w-3.5 shrink-0 text-brand-300" strokeWidth={2} />
            <span className="max-w-[104px] truncate font-medium">
              {selectedModel?.label ?? modelId}
            </span>
            <ChevronDown
              className={cn('h-3.5 w-3.5 text-white/40 transition', pickerOpen && 'rotate-180')}
              strokeWidth={2}
            />
          </button>

          {selectedModel?.tier === 'fast' && (
            <span className="pointer-events-none absolute right-0 top-full mt-1 whitespace-nowrap text-[9px] text-white/25">
              сложные задачи усиливаются автоматически
            </span>
          )}

          {pickerOpen && (
            <div
              role="listbox"
              className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-white/10 bg-[#160c26] p-1 shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            >
              <div
                className="flex items-center gap-1.5 px-2.5 pb-1.5 pt-1 text-[10px] text-white/35"
                title="Цена — в наших токенах ($) за 1M токенов, вход / выход, с учётом наценки"
              >
                <Info className="h-3 w-3 shrink-0" strokeWidth={2} />
                <span>Цена за 1M токенов · вход / выход</span>
              </div>
              {TIER_ORDER.map((tier) => {
                const TierIcon = TIER_ICON[tier];
                return (
                  <div key={tier} className="py-0.5">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/35">
                      <TierIcon className="h-3 w-3 shrink-0" strokeWidth={2.2} />
                      {TIER_LABEL[tier]}
                    </div>
                    {AGENT_MODELS.filter((m) => m.tier === tier).map((m) => {
                      const active = m.id === modelId;
                      const price = pricePer1M(m);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => {
                            setModelId(m.id);
                            setPickerOpen(false);
                          }}
                          className={cn(
                            'flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left transition',
                            active ? 'bg-brand-600/20 text-brand-100' : 'text-white/80 hover:bg-white/5'
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px]">{m.label}</span>
                            <span className="block text-[10px] tabular-nums text-white/35">
                              {price.input} / {price.output}
                            </span>
                          </span>
                          {active && (
                            <Check className="h-3.5 w-3.5 shrink-0 text-brand-300" strokeWidth={2.5} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* spent-on-chat counter (from persisted history usage) + generation spend */}
      {(spentUsd > 0 || spentGenUsd > 0) && (
        <div className="px-4 pb-2 text-[11px] text-white/35">
          Потрачено на чат:{' '}
          <span className="tabular-nums text-white/60">{spentUsd.toFixed(4)} ток.</span>
          {spentGenUsd > 0 && (
            <>
              {' · '}генерация: <span className="tabular-nums text-white/60">{spentGenUsd.toFixed(2)} ток.</span>
            </>
          )}
        </div>
      )}

      {/* conversation */}
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-2">
        {history.length === 0 && messages.length === 0 && !isBusy ? (
          <div className="my-6 text-center text-xs leading-relaxed text-white/35">
            {t('studio.editor.chat.empty')}
          </div>
        ) : (
          <div className="my-1 text-center text-[11px] text-white/30">{t('studio.editor.chat.today')}</div>
        )}

        {messages.map((m) => {
          const parts = (m as { parts?: unknown }).parts;
          const text = textFromParts(parts);
          const actions = toolActionsFromParts(parts);
          const isUser = m.role === 'user';
          const proposeParts = isUser ? [] : proposeGenerationPartsFrom(parts);
          const showSent = isUser && m.id === lastUserMsgId && lastSent;
          return (
            <div key={m.id} className="flex flex-col">
              <ChatBubble
                role={isUser ? 'user' : 'assistant'}
                text={text}
                actions={isUser ? undefined : actions}
              />
              {showSent && lastSent && (
                <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
                  {lastSent.imageUrls?.map((url, i) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={`si-${i}`}
                      src={url}
                      alt="вложение"
                      className="h-14 w-14 rounded-lg border border-white/10 object-cover"
                    />
                  ))}
                  {lastSent.docTexts?.map((d, i) => (
                    <span
                      key={`sd-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-white/60"
                    >
                      <FileText className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                      <span className="max-w-[120px] truncate">{d.name}</span>
                    </span>
                  ))}
                  {lastSent.mentions?.map((mn, i) => (
                    <span
                      key={`sm-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-white/60"
                    >
                      <Film className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                      <span className="max-w-[120px] truncate">{mn.title}</span>
                    </span>
                  ))}
                </div>
              )}
              {proposeParts.map((part) => {
                const proposal = genProposals.find((p) => p.toolCallId === part.toolCallId);
                // `createGenProposal` is still quoting (or hasn't been kicked
                // off by the effect above yet) — nothing to render until the
                // store has a proposal for this toolCallId.
                if (!proposal) return null;
                return (
                  <GenerationProposalCard
                    key={part.toolCallId}
                    proposal={proposal}
                    onAllow={() => {
                      allowGenProposal(part.toolCallId, authedFetch);
                      void addToolOutput({
                        tool: 'proposeGeneration',
                        toolCallId: part.toolCallId,
                        output: `allowed: запущено ${part.input.shots.length} шотов`,
                      });
                    }}
                    onDecline={() => {
                      declineGenProposal(part.toolCallId);
                      void addToolOutput({
                        tool: 'proposeGeneration',
                        toolCallId: part.toolCallId,
                        output: 'declined',
                      });
                    }}
                    onRetryJob={(localId) => retryGenJob(part.toolCallId, localId, authedFetch)}
                  />
                );
              })}
            </div>
          );
        })}

        {restoredProposals.length > 0 && (
          <div className="mt-1 flex flex-col gap-2 border-t border-white/[0.06] pt-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/30">
              Задачи проекта
            </div>
            {restoredProposals.map((proposal) => (
              <GenerationProposalCard
                key={proposal.toolCallId}
                proposal={proposal}
                onAllow={() => allowGenProposal(proposal.toolCallId, authedFetch)}
                onDecline={() => declineGenProposal(proposal.toolCallId)}
                onRetryJob={(localId) => retryGenJob(proposal.toolCallId, localId, authedFetch)}
              />
            ))}
          </div>
        )}

        {isBusy && (
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-300" strokeWidth={2} />
            {t('studio.editor.chat.thinking')}
          </div>
        )}

        {status === 'error' && error && (
          <div className="ml-8 rounded-xl border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2.5 text-[12px] text-amber-100/80">
            <p>{friendlyAgentError(error)}</p>
            {hasPendingProposal ? (
              // Retrying while a proposeGeneration tool-call is unresolved would
              // resubmit a message list with an open tool call (provider-400 /
              // chat corruption). Make the user resolve the plan card first.
              <p className="mt-2 text-[11px] text-white/45">
                Сначала разрешите или отклоните план выше.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (hasPendingProposal) return;
                  retry();
                }}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-300/20 bg-white/[0.05] px-2.5 py-1.5 text-[11px] font-medium text-white/80 transition hover:bg-white/10"
              >
                <RefreshCw className="h-3 w-3" strokeWidth={2.2} />
                Повторить ответ
              </button>
            )}
          </div>
        )}
      </div>

      {/* input */}
      {hasPendingProposal && status === 'ready' && (
        <div className="px-4 pb-1 text-[11px] text-white/35">
          Сначала решите по плану генерации выше (Разрешить / Отклонить)
        </div>
      )}
      <ChatComposer
        onSend={(text, extras) => sendMessage(text, extras)}
        disabled={isBusy || hasPendingProposal}
        busy={isBusy}
      />
    </div>
  );
}
